<template>
  <div>
    <ui-stage>
      <ui-flex row a-center class="mb">
        <div>
          <h3>项目</h3>
          <ui-admin-model-picker v-model="projectId" :config="projectPickerConfig" />
        </div>
        <div class="ml">
          <h3>状态</h3>
          <el-select v-model="stateFilter" clearable placeholder="全部" style="width: 160px">
            <el-option label="待处理" value="待处理" />
            <el-option label="已整改" value="已整改" />
            <el-option label="已处罚" value="已处罚" />
            <el-option label="无需整改" value="无需整改" />
          </el-select>
        </div>
        <div class="ml">
          <h3>类型</h3>
          <el-select v-model="typeFilter" clearable placeholder="全部" style="width: 200px">
            <el-option label="日常巡查-上午" value="日常巡查-上午" />
            <el-option label="日常巡查-下午" value="日常巡查-下午" />
            <el-option label="问题记录" value="问题记录" />
          </el-select>
        </div>
      </ui-flex>

      <div class="mb stats">
        <ui-flex row gap>
          <div class="stat-card">
            <div class="stat-card__number">{{ stats.today }}</div>
            <div class="stat-card__label">项目今日</div>
          </div>
          <div class="stat-card">
            <div class="stat-card__number">{{ stats.todo }}</div>
            <div class="stat-card__label">待处理</div>
          </div>
          <div class="stat-card">
            <div class="stat-card__number">{{ stats.done }}</div>
            <div class="stat-card__label">已整改</div>
          </div>
        </ui-flex>
      </div>

      <el-alert
        v-if="isReadonlyViewer"
        type="info"
        :closable="false"
        show-icon
        class="mb"
      >
        您当前为只读模式查看监理巡查数据，仅监理单位可新建与修改。
      </el-alert>

      <ui-admin-table
        v-if="data"
        :attributes="attributes"
        :data="data"
        :module-url="moduleUrl"
        pagination-method="front-end"
        :with-create="canCreate"
        :with-delete="canDelete"
        :is-edit-view="true"
      >
        <template #photos="{ row }">
          <PhotoCell :photos="row.photos" />
        </template>
      </ui-admin-table>
    </ui-stage>
  </div>
</template>

<script>
import { _, moment } from '@yishitec/web';
import UiAdminModelPicker from '@yishitec/web/components/UiAdminModelPicker.vue';
import { adminList } from '@yishitec/web/mixins';
import permissions from '../../../mixins/permissions';
import PhotoCell from '../../../components/PhotoCell.vue';

const projectPickerConfig = {
  title: '项目',
  name: 'projectId',
  model: {
    name: 'Project',
    titleFields: ['title'],
    type: 'string',
    appData: {
      apiName: 'projects',
    },
  },
  type: 'model',
};

export default {
  name: 'SupervisionPlogsList',
  components: { UiAdminModelPicker, PhotoCell },
  mixins: [adminList, permissions],
  data() {
    return {
      projectId: '',
      projectPickerConfig,
      stateFilter: '',
      typeFilter: '',
      stats: { today: 0, todo: 0, done: 0 },
    };
  },
  computed: {
    canCreate() {
      return this.amI(this.projectId, '监理单位', '管理人员', true) || this.isSuperAdmin;
    },
    canDelete() {
      return this.canCreate;
    },
    /** 当前用户是否为只读查看方（建设单位 / 其他只读角色） */
    isReadonlyViewer() {
      if (this.isSuperAdmin) return false;
      // 监理单位全部角色 / 施工单位管理员可写；其余视为只读
      return !this.amI(this.projectId, '监理单位');
    },
    filteredData() {
      if (!this.data) return [];
      let list = this.data;
      if (this.stateFilter) list = list.filter((it) => it.state === this.stateFilter);
      if (this.typeFilter) list = list.filter((it) => it.type === this.typeFilter);
      return list;
    },
  },
  watch: {
    currentProjects(value) {
      const next = _.get(value, '0');
      if (next && !this.projectId) this.projectId = next;
    },
    projectId() {
      this.refresh();
      this.loadStats();
    },
    stateFilter() {
      this.refresh();
    },
    typeFilter() {
      this.refresh();
    },
  },
  mounted() {
    const init = _.get(this, 'currentProjects.0');
    if (init) this.projectId = init;
  },
  methods: {
    async refresh() {
      this.apiBody = {};
      this.apiBodyData = {};
      await this.fetchData();
    },
    async fetchData() {
      // override: 把项目 id / state / type 拼到 where 条件
      const where = {};
      if (this.projectId) where.projectId = this.projectId;
      if (this.stateFilter) where.state = this.stateFilter;
      if (this.typeFilter) where.type = this.typeFilter;
      const res = await this.withLoading(
        this.api(this.vApi, {
          ...this.apiBody,
          ...where,
        }),
      );
      if (!res) {
        this.$message({ type: 'error', message: '无法加载列表' });
        return;
      }
      const { attributes, list } = res;
      this.attributes = _.filter(
        await this.parseAttributes(attributes),
        (attr) => _.indexOf(this.hiddenColumns || [], attr.name) === -1,
      );
      this.data = await this.parseListData(list);
    },
    async loadStats() {
      if (!this.projectId) {
        this.stats = { today: 0, todo: 0, done: 0 };
        return;
      }
      try {
        const res = await this.withLoading(
          this.api('/api/supervision-plogs/list', { projectId: this.projectId }),
        );
        const list = _.get(res, 'list', []);
        const startOfDay = moment().startOf('day').valueOf();
        this.stats = {
          today: list.filter((it) => (it.checkTime || it.createdAt) >= startOfDay).length,
          todo: list.filter((it) => it.state === '待处理').length,
          done: list.filter(
            (it) => it.state && it.state !== '待处理',
          ).length,
        };
      } catch (e) {
        // 静默失败
      }
    },
  },
};
</script>

<style lang="less" scoped>
.ml {
  margin-left: 12px;
}
.stats {
  .stat-card {
    background: #f7f9fc;
    border: 1px solid #ecf0f6;
    border-radius: 6px;
    padding: 10px 20px;
    min-width: 120px;
    text-align: center;
    &__number {
      font-size: 22px;
      font-weight: 600;
      color: #303133;
    }
    &__label {
      font-size: 12px;
      color: #909399;
      margin-top: 4px;
    }
  }
}
</style>
